import { flip, offset, shift, useFloating } from "@floating-ui/react-dom";
import { type Editor } from "@tiptap/core";
import { Skeleton } from "antd";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";

import { BTLoginTypes } from "@btnet/services-btnet/handlers/models/bTLoginTypes";

import { useRequiredContext } from "helpers/globalContext/useRequiredContext";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { BTUserInfoDisplay } from "commonComponents/btWrappers/BTUser/BTUserInfoDisplay";
import { getDOMPositionOfSelection } from "commonComponents/btWrappers/editor/core/editor.utils";
import { type IMentionUserAttributes } from "commonComponents/btWrappers/editor/core/extensions/custom/mentionUser/MentionUserExtension";

import { UserActivationPageHandler } from "entity/LinkAccountActivation/UserActivationPage.api.handler";
import { type UserProfileResponse } from "entity/login/Login/Login.api.types";

import "./MentionUserOverlay.less";

interface IMentionUserOverlayProps {
    editor: Editor;
    nodeAttrs: IMentionUserAttributes;
    userHandler?: UserActivationPageHandler;
    onClose?: () => void;
}

const defaultUserHandler = new UserActivationPageHandler();

export const MentionUserOverlay = ({
    editor,
    nodeAttrs,
    userHandler = defaultUserHandler,
    onClose,
}: IMentionUserOverlayProps) => {
    const userInfo = useRequiredContext(UserInfoContext);
    const { x, y, strategy, refs } = useFloating({
        middleware: [flip(), shift({ padding: 5 }), offset(5)],
    });

    // Automatically close the overlay when clicking outside of it or scrolling
    useEffect(() => {
        if (!onClose) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (!refs.floating.current?.contains(event.target as Node)) {
                onClose();
            }
        };

        const handleScroll = () => {
            onClose();
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("scroll", handleScroll, true);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("scroll", handleScroll, true);
        };
    }, [onClose, refs.floating]);

    const handleCalculatePosition = useCallback(() => {
        return getDOMPositionOfSelection(editor);
    }, [editor]);

    useLayoutEffect(() => {
        refs.setReference({ getBoundingClientRect: handleCalculatePosition });
    }, [handleCalculatePosition, refs]);

    const [userProfile, setUserProfile] = useState<UserProfileResponse | null>(null);
    const handleLoadUser = useCallback(async () => {
        let profile = null;
        if (nodeAttrs.id && userInfo.loginType !== BTLoginTypes.SUBS) {
            profile = await userHandler.profile(nodeAttrs.id);
        } else {
            profile = {
                name: nodeAttrs.label,
                userType: BTLoginTypes.ALL,
                phone: "",
                email: "",
                title: "",
                cellPhone: "",
                primaryContact: "",
                canStartChatConversation: false,
            };
        }
        setUserProfile(profile);
    }, [nodeAttrs.id, nodeAttrs.label, userHandler, userInfo.loginType]);

    useEffect(() => {
        void handleLoadUser();
    }, [handleLoadUser]);

    let display = <Skeleton.Button style={{ height: "96px", width: "350px" }} />;

    if (userProfile) {
        display = (
            <BTUserInfoDisplay
                showManageUser
                hideAllControls={false}
                globalUserId={nodeAttrs.id}
                displayName={userProfile.name}
                userProfile={userProfile}
            />
        );
    }

    return (
        <div
            ref={(ref) => {
                refs.setFloating(ref);
            }}
            className="MentionUserOverlay"
            data-testid="mentionUserOverlay"
            style={{
                position: strategy,
                top: y ?? undefined,
                left: x ?? undefined,
            }}
        >
            {display}
        </div>
    );
};
